// HousekeepingDashboard.js  Live room status grid + active/delayed tasks +
// staff workload  modeled after Opera PMS HK floor board.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, Grid, Chip, Snackbar, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { housekeepingApi, ctx } from './modulesApi';

const STATUS_COLORS = {
  Clean: 'success', Ready: 'success',
  Dirty: 'error', UnderMaintenance: 'warning',
  Occupied: 'info', Vacant: 'default', InspectionPending: 'secondary',
};

export default function HousekeepingDashboard() {
  const { companyId, hotelId } = ctx();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const load = async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const r = await housekeepingApi.dashboard(companyId, hotelId);
      setData(r.data);
    } catch { setSnack({ open: true, msg: 'Load failed', sev: 'error' }); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // auto-refresh every 60s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [hotelId]);

  if (loading || !data) {
    return (
      <Container maxWidth='xl' sx={{ py: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Box display='flex' alignItems='center' gap={1} mb={2}>
        <DashboardIcon color='primary' />
        <Typography variant='h5'>Housekeeping Dashboard</Typography>
        <Chip size='small' label={`auto-refresh 60s`} sx={{ ml: 2 }} />
      </Box>

      {/* Room status summary */}
      <Grid container spacing={2} mb={3}>
        {data.roomStatusCounts.map((s) => (
          <Grid item xs={6} sm={3} md={2} key={s.status}>
            <Card>
              <CardContent>
                <Typography variant='caption' color='textSecondary'>{s.status}</Typography>
                <Typography variant='h4'>{s.cnt}</Typography>
                <Chip size='small' label={s.status} color={STATUS_COLORS[s.status] || 'default'} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant='h6' mb={1}>Active Tasks</Typography>
              <TableContainer><Table size='small'>
                <TableHead><TableRow>
                  <TableCell>Room</TableCell><TableCell>Task</TableCell>
                  <TableCell>Priority</TableCell><TableCell>Staff</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.activeTasks.length === 0 && (<TableRow><TableCell colSpan={5} align='center'>No active tasks</TableCell></TableRow>)}
                  {data.activeTasks.map((t) => (
                    <TableRow key={t.taskId}>
                      <TableCell>{t.roomNumber}</TableCell>
                      <TableCell>{t.taskType}</TableCell>
                      <TableCell>
                        <Chip size='small' label={t.priority}
                          color={t.priority === 'Urgent' ? 'error' : t.priority === 'High' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>{t.staffName}</TableCell>
                      <TableCell><Chip size='small' label={t.status}
                        color={t.status === 'InProgress' ? 'warning' : 'info'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant='h6' mb={1}>Delayed Tasks ({data.delayedTasks.length})</Typography>
              <TableContainer><Table size='small'>
                <TableHead><TableRow>
                  <TableCell>Room</TableCell><TableCell>Task</TableCell>
                  <TableCell>Status</TableCell><TableCell>Note</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.delayedTasks.length === 0 && (<TableRow><TableCell colSpan={4} align='center'>None</TableCell></TableRow>)}
                  {data.delayedTasks.map((t) => (
                    <TableRow key={t.taskId} sx={{ backgroundColor: '#fff8e1' }}>
                      <TableCell>{t.roomNumber}</TableCell>
                      <TableCell>{t.taskType}</TableCell>
                      <TableCell><Chip size='small' color='warning' label={t.status} /></TableCell>
                      <TableCell><small>{t.remarks}</small></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant='h6' mb={1}>Staff Workload</Typography>
              <TableContainer><Table size='small'>
                <TableHead><TableRow>
                  <TableCell>Staff</TableCell><TableCell>Shift</TableCell>
                  <TableCell align='right'>Open Tasks</TableCell>
                  <TableCell align='right'>Completed Today</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.staffWorkload.map((s) => (
                    <TableRow key={s.staffId}>
                      <TableCell>{s.staffName}</TableCell>
                      <TableCell>{s.shift}</TableCell>
                      <TableCell align='right'>{s.openTasks}</TableCell>
                      <TableCell align='right'>{s.completedToday}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
